import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BankAccountWithMandatesDto } from "../../../../stores/Sepa/sepa.types";
import { useUpdateSepaMandate } from "../../../../stores/Sepa/sepa.mutations";
import BankAccountSelector, { BankSelection } from "./BankAccountSelector";
import { bankSelectionToDto, validateBankSelection } from "./bankSelection";

interface Props {
  patientId: number;
  mandateId: string;
  /** The mandate's current bank account, used to prefill the editable fields. */
  account: BankAccountWithMandatesDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Corrects a DRAFT mandate's bank details before it is signed. Only drafts reach
 * this dialog; active mandates are locked server-side (a correction there needs a
 * new mandate).
 */
const MandateEditDialog: React.FC<Props> = ({
  patientId,
  mandateId,
  account,
  open,
  onOpenChange,
}) => {
  const update = useUpdateSepaMandate(patientId);
  const [bank, setBank] = useState<BankSelection>({ mode: "existing", bankAccountId: account.id });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      // Prefill as editable details so the user can correct the holder/IBAN/BIC.
      setBank({
        mode: "new",
        holderFirstName: account.holderFirstName,
        holderLastName: account.holderLastName,
        iban: account.iban,
        bic: account.bic ?? "",
      });
      setErrors({});
    }
  }, [open, account]);

  const handleSubmit = () => {
    const nextErrors = validateBankSelection(bank);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    update.mutate(
      { id: mandateId, dto: bankSelectionToDto(bank) },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl">Mandat bearbeiten</DialogTitle>
          <DialogDescription className="text-[15px]">
            Bankdaten des Entwurfs korrigieren. Nur möglich, solange das Mandat noch nicht
            unterschrieben und aktiviert wurde.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <BankAccountSelector
            patientId={patientId}
            value={bank}
            onChange={setBank}
            errors={errors}
            disabled={update.isPending}
          />
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={update.isPending}
          >
            Abbrechen
          </Button>
          <Button
            className="bg-blue-darkest text-white hover:bg-blue-darkest/90"
            onClick={handleSubmit}
            disabled={update.isPending}
          >
            {update.isPending ? "Wird gespeichert…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MandateEditDialog;
